import { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightColors, darkColors, setColorMode } from '../colors';

const ThemeContext = createContext(null);

function buildMuiTheme(colors, mode) {
  return createTheme({
    palette: {
      mode,
      primary:    { main: colors.primary, light: colors.primaryLight, dark: colors.primaryDark, contrastText: colors.textInverse },
      secondary:  { main: colors.secondary, light: colors.secondaryLight, dark: colors.secondaryDark, contrastText: colors.textInverse },
      info:       { main: colors.info },
      success:    { main: colors.success },
      warning:    { main: colors.warning },
      error:      { main: colors.error },
      background: { default: colors.bgDefault, paper: colors.bgPaper },
      text:       { primary: colors.textPrimary, secondary: colors.textSecondary },
      divider:    colors.border,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif',
      h4: { fontWeight: 800 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: colors.bgPaper,
            borderBottom: `1px solid ${colors.border}`,
            boxShadow: 'none',
            color: colors.textPrimary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 8, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: { background: colors.primary, '&:hover': { background: colors.primaryHover } },
          sizeLarge: { padding: '10px 22px', fontSize: '0.9375rem' },
          sizeSmall: { padding: '6px 12px', fontSize: '0.8125rem' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', border: `1px solid ${colors.border}` },
          elevation0: { border: 'none' },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.bgPaper,
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.borderStrong },
              '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1 },
            },
            '& .MuiInputLabel-root': { color: colors.textSecondary },
            '& .MuiInputBase-input': {
              color: colors.textPrimary,
              '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
                WebkitBoxShadow: `0 0 0 1000px ${colors.bgPaper} inset`,
                WebkitTextFillColor: colors.textPrimary,
                caretColor: colors.textPrimary,
                borderRadius: 'inherit',
              },
            },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    },
  });
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('theme-mode') || 'dark';
  });

  // Sync the cached palette in colors.js synchronously during render so
  // components reading the `colors` proxy see the new palette in the same
  // commit as the MUI theme switch. Doing this in a useEffect would cause
  // a one-frame mismatch where MUI components (e.g. AppBar) flip while
  // proxy-consuming components (e.g. Chat) still show the previous theme.
  setColorMode(mode);

  const toggleTheme = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme-mode', next);
      return next;
    });
  };

  const colors = mode === 'dark' ? darkColors : lightColors;
  const muiTheme = useMemo(() => buildMuiTheme(colors, mode), [mode, colors]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, colors }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within AppThemeProvider');
  return ctx; // { mode, toggleTheme, colors }
}

/**
 * Hook to get the active color palette.
 * Use this in components instead of importing `colors` directly.
 */
export function useColors() {
  const { colors } = useThemeMode();
  return colors;
}
